import { type LandingSectionDefinition, type LandingSectionField } from "../../fields";
import {
  bikini2026CheckoutProps,
  bikini2026FaqProps,
  bikini2026FooterProps,
  bikini2026HeaderProps,
  bikini2026HeroProps,
  bikini2026IncludedProps,
  bikini2026MetamorphosesProps,
  bikini2026OfferProps,
  bikini2026PainProps,
  bikini2026RewardProps,
  bikini2026StickyCtaProps,
  bikini2026TimelineProps,
  bikini2026TransformationsProps,
  bikiniFunnelCheckoutProps,
  bikiniFunnelThankYouProps,
} from "./content";
import { type bikini2026SectionTypes } from "./types";

const GROUP = "6 Tygodni do Bikini 2026";
const CLASSIC_GROUP = "6 Tygodni do Bikini";

const eyebrow: LandingSectionField = { key: "eyebrow", label: "Nadtytuł (czerwony)", type: "text" };
const title: LandingSectionField = {
  key: "title",
  label: "Tytuł",
  type: "richText",
  help: "<strong> = pogrubiony fragment.",
};
const transformationIds: LandingSectionField = {
  key: "transformationIds",
  label: "ID metamorfoz",
  type: "stringList",
  help: "Jedno ID na linię, w tej kolejności (dane z listy metamorfoz Programu Hollywood).",
};

export const bikini2026SectionDefinitions: Record<(typeof bikini2026SectionTypes)[number], LandingSectionDefinition> = {
  bikini2026Header: {
    label: "Nagłówek z logo",
    group: GROUP,
    fields: [
      { key: "ctaLabel", label: "Przycisk (przewija do oferty)", type: "text" },
      {
        key: "hidden",
        label: "Niewidoczny (zajmuje miejsce)",
        type: "boolean",
        help: "Tak jak na stronie z kodu: pasek jest ukryty, zostaje tylko odstęp nad hero.",
      },
    ],
    defaultProps: bikini2026HeaderProps,
  },
  bikini2026Hero: {
    label: "Hero",
    group: GROUP,
    fields: [
      { key: "badge", label: "Plakietka nad tytułem", type: "text" },
      {
        key: "title",
        label: "Tytuł",
        type: "richText",
        help: "<strong> = pogrubienie, <em> = fraza podkreślona falką.",
      },
      { key: "subtitle", label: "Podtytuł", type: "textarea" },
      { key: "bullets", label: "Punkty z ✅", type: "stringList" },
      {
        key: "savingsBadge",
        label: "Plakietka rabatu nad przyciskiem",
        type: "text",
        help: "Widoczna, gdy cena funnelu ma rabat. {{procentRabatu}} = procent rabatu.",
      },
      { key: "ctaLabel", label: "Przycisk", type: "text" },
      {
        key: "ctaPriceNote",
        label: "Dopisek po cenie w przycisku",
        type: "text",
        help: "Pod etykietą przycisku: cena przed rabatem, cena funnelu i ten dopisek.",
      },
      {
        key: "trust",
        label: "Pasek zaufania",
        type: "list",
        itemLabel: "Element",
        fields: [
          { key: "icon", label: "Emoji", type: "text" },
          { key: "boldText", label: "Pogrubiony początek", type: "text" },
          { key: "rest", label: "Reszta", type: "text" },
        ],
      },
      { key: "href", label: "Link przycisku", type: "url", help: "Puste = przewija do oferty." },
    ],
    defaultProps: bikini2026HeroProps,
  },
  bikini2026Transformations: {
    label: "Karuzela metamorfoz",
    group: GROUP,
    fields: [transformationIds],
    defaultProps: bikini2026TransformationsProps,
  },
  bikini2026Pain: {
    label: "Znasz to? (problemy)",
    group: GROUP,
    fields: [
      eyebrow,
      title,
      { key: "text", label: "Opis", type: "textarea" },
      { key: "points", label: "Problemy (z ✕)", type: "stringList" },
    ],
    defaultProps: bikini2026PainProps,
  },
  bikini2026Included: {
    label: "Co dostajesz (kafelki)",
    group: GROUP,
    fields: [
      eyebrow,
      title,
      {
        key: "items",
        label: "Kafelki",
        type: "list",
        itemLabel: "Kafelek",
        fields: [
          { key: "icon", label: "Emoji", type: "text" },
          { key: "title", label: "Tytuł", type: "text" },
          { key: "text", label: "Opis", type: "textarea" },
        ],
      },
    ],
    defaultProps: bikini2026IncludedProps,
  },
  bikini2026Timeline: {
    label: "Oś czasu 6 tygodni",
    group: GROUP,
    fields: [
      eyebrow,
      title,
      {
        key: "steps",
        label: "Etapy",
        type: "list",
        itemLabel: "Etap",
        fields: [
          { key: "week", label: "Kiedy", type: "text" },
          { key: "title", label: "Tytuł", type: "text" },
          { key: "text", label: "Opis", type: "textarea" },
        ],
      },
    ],
    defaultProps: bikini2026TimelineProps,
  },
  bikini2026Reward: {
    label: "Nagroda 300% (ciemny pas)",
    group: GROUP,
    fields: [
      eyebrow,
      title,
      { key: "text", label: "Opis", type: "textarea" },
      {
        key: "rewardMultiplier",
        label: "Mnożnik nagrody",
        type: "number",
        help: "{{nagroda}} = cena jednorazowa funnelu razy ten mnożnik.",
      },
      {
        key: "items",
        label: "Punkty",
        type: "list",
        itemLabel: "Punkt",
        fields: [
          { key: "icon", label: "Emoji", type: "text" },
          { key: "text", label: "Tekst", type: "richText", help: "{{nagroda}} = kwota nagrody." },
          {
            key: "textWithoutPrice",
            label: "Tekst bez ceny",
            type: "richText",
            help: "Gdy funnel nie ma ceny (puste = ten sam tekst).",
          },
        ],
      },
    ],
    defaultProps: bikini2026RewardProps,
  },
  bikini2026Metamorphoses: {
    label: "Siatka metamorfoz z wideo",
    group: GROUP,
    fields: [eyebrow, title, { key: "text", label: "Opis", type: "textarea" }, transformationIds],
    defaultProps: bikini2026MetamorphosesProps,
  },
  bikini2026Offer: {
    label: "Oferta z ceną",
    group: GROUP,
    fields: [
      title,
      { key: "benefits", label: "Korzyści (→)", type: "stringList", help: "<strong> = pogrubienie." },
      { key: "eyebrow", label: "Nadtytuł karty", type: "text" },
      { key: "productName", label: "Nazwa produktu", type: "text" },
      { key: "regularPriceLabel", label: "Etykieta ceny regularnej", type: "text" },
      {
        key: "savingsLabel",
        label: "Plakietka oszczędności",
        type: "text",
        help: "{{oszczednosc}}, {{procentRabatu}}.",
      },
      { key: "currentPriceLabel", label: "Etykieta ceny po rabacie", type: "text" },
      { key: "priceLabel", label: "Etykieta ceny bez rabatu", type: "text" },
      { key: "installmentsLabel", label: "Raty", type: "richText", help: "{{raty}}." },
      { key: "ctaLabel", label: "Przycisk", type: "text", help: "Po etykiecie dopisujemy cenę funnelu." },
      { key: "ctaNote", label: "Druga linia przycisku", type: "text" },
      { key: "footnotes", label: "Dopiski pod przyciskiem", type: "stringList" },
      { key: "href", label: "Link przycisku", type: "url", help: "Puste = checkout funnelu." },
    ],
    defaultProps: bikini2026OfferProps,
  },
  bikini2026Faq: {
    label: "FAQ",
    group: GROUP,
    fields: [
      eyebrow,
      title,
      {
        key: "items",
        label: "Pytania",
        type: "list",
        itemLabel: "Pytanie",
        fields: [
          { key: "q", label: "Pytanie", type: "text" },
          { key: "a", label: "Odpowiedź", type: "textarea" },
        ],
      },
    ],
    defaultProps: bikini2026FaqProps,
  },
  bikini2026Footer: {
    label: "Stopka",
    group: GROUP,
    fields: [
      { key: "metamorphosesCount", label: "Licznik metamorfoz", type: "text" },
      { key: "copyright", label: "Copyright", type: "text" },
    ],
    defaultProps: bikini2026FooterProps,
  },
  bikini2026StickyCta: {
    label: "Przyklejony pasek na telefonie",
    group: GROUP,
    fields: [
      { key: "productLabel", label: "Nazwa nad ceną", type: "text" },
      { key: "noteWithDiscount", label: "Dopisek pod ceną (z rabatem)", type: "text", help: "{{cenaPrzedRabatem}}." },
      { key: "note", label: "Dopisek pod ceną (bez rabatu)", type: "text" },
      { key: "ctaLabel", label: "Przycisk (przewija do oferty)", type: "text" },
    ],
    defaultProps: bikini2026StickyCtaProps,
  },
  bikini2026Checkout: {
    label: "Checkout w stylu Hollywood",
    group: GROUP,
    fields: [
      { key: "seoTitle", label: "Tytuł karty przeglądarki", type: "text" },
      { key: "heroTitle", label: "Tytuł", type: "richText", help: "<strong> = pogrubienie." },
      { key: "heroSubtitle", label: "Podtytuł", type: "textarea" },
      { key: "productIcon", label: "Emoji produktu", type: "text" },
      { key: "productName", label: "Nazwa produktu", type: "text" },
      { key: "productTagline", label: "Opis produktu", type: "textarea" },
      { key: "productItems", label: "Co zawiera (rozwijane)", type: "stringList" },
      { key: "frameText", label: "Ramka obietnicy", type: "richText" },
      { key: "frameDetails", label: "Ramka: „Jak to działa?”", type: "textarea" },
      { key: "phoneHelpText", label: "Pomoc pod telefonem", type: "text" },
      {
        key: "afterPaymentSteps",
        label: "Co dalej po płatności",
        type: "list",
        itemLabel: "Krok",
        fields: [
          { key: "when", label: "Kiedy", type: "text" },
          { key: "what", label: "Co", type: "text" },
        ],
      },
      {
        key: "faq",
        label: "FAQ pod formularzem",
        type: "list",
        itemLabel: "Pytanie",
        fields: [
          { key: "q", label: "Pytanie", type: "text" },
          { key: "a", label: "Odpowiedź", type: "textarea" },
          {
            key: "installmentsNote",
            label: "Dopisek przy ratach",
            type: "text",
            help: "Dopisywany do odpowiedzi przy płatności w ratach. {{rata}} = kwota kolejnej raty.",
          },
        ],
      },
      { key: "stickyLabel", label: "Nazwa w pasku na telefonie", type: "text" },
      { key: "payButtonLabel", label: "Przycisk płatności", type: "text", help: "{{kwota}} = kwota do zapłaty dziś." },
      { key: "payButtonNote", label: "Druga linia przycisku płatności", type: "text" },
    ],
    defaultProps: bikini2026CheckoutProps,
  },
  bikiniFunnelCheckout: {
    label: "Checkout Bikini (formularz płatności)",
    group: CLASSIC_GROUP,
    fields: [
      { key: "heroTitle", label: "Tytuł", type: "richText", help: "<em> = różowy fragment." },
      { key: "heroSubtitle", label: "Podtytuł", type: "text" },
      { key: "participantsBadge", label: "Plakietka z liczbą uczestniczek", type: "text" },
      { key: "rewardBadge", label: "Plakietka dofinansowania", type: "text" },
      { key: "ctaText", label: "Przycisk płatności (przed ceną)", type: "text" },
      {
        key: "priceDescription",
        label: "Opis pod ceną",
        type: "textarea",
        help: "Linie oddzielone <br/>.",
      },
      {
        key: "securityItems",
        label: "Kafelki bezpieczeństwa",
        type: "list",
        itemLabel: "Kafelek",
        fields: [
          { key: "icon", label: "Emoji", type: "text" },
          { key: "text", label: "Tekst", type: "text" },
        ],
      },
      { key: "benefitsTitle", label: "Tytuł listy „dla Ciebie”", type: "richText", help: "<em> = różowy fragment." },
      { key: "benefits", label: "Lista „dla Ciebie”", type: "stringList" },
      {
        key: "stickyCta",
        label: "Przyklejony przycisk na telefonie",
        type: "select",
        options: [
          { label: "Według eksperymentu PostHog (bikini-landing-variant)", value: "experiment" },
          { label: "Zawsze", value: "always" },
          { label: "Nigdy", value: "never" },
        ],
      },
      { key: "stickyProductLabel", label: "Pasek: nazwa produktu", type: "text" },
      { key: "stickyCtaText", label: "Pasek: przycisk", type: "text" },
    ],
    defaultProps: bikiniFunnelCheckoutProps,
  },
  bikiniFunnelThankYou: {
    label: "Podziękowanie Bikini",
    group: CLASSIC_GROUP,
    fields: [
      { key: "titleHighlight", label: "Tytuł: różowy początek", type: "text" },
      { key: "title", label: "Tytuł: reszta", type: "text" },
      { key: "subtitle", label: "Podtytuł", type: "richText", help: "<strong> = różowe pogrubienie." },
      {
        key: "steps",
        label: "Kroki",
        type: "list",
        itemLabel: "Krok",
        fields: [
          {
            key: "icon",
            label: "Ikona",
            type: "select",
            options: [
              { label: "Koperta", value: "mail" },
              { label: "Telefon", value: "smartphone" },
              { label: "Grupa", value: "users" },
            ],
          },
          { key: "text", label: "Tekst", type: "richText", help: "<strong> = pogrubienie, <u> = podkreślenie." },
        ],
      },
      { key: "contact", label: "Kontakt", type: "richText" },
      { key: "confetti", label: "Konfetti po wejściu", type: "boolean" },
    ],
    defaultProps: bikiniFunnelThankYouProps,
  },
};
